// -----------------------------------------------------------------------------
// Combined topbar notifications drawer.
//
// Funnels every notification source the dashboard cares about - work
// orders + chat (and anything we add later) - into a single dropdown
// shown next to the search bar. Each row is a "thread": for chat that's
// a channel/DM with its latest unread message preview; for work orders
// that's whatever payload `/notification/workorders` already produces.
//
// New chat messages are pushed in via SignalR (see useChatNotifications)
// so the badge updates in real time on every route, not just /chat.
// -----------------------------------------------------------------------------

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ComputerDesktopIcon } from "@heroicons/react/24/outline";

import NotificationsDropdown, { AppNotification } from "./NotificationsDropdown";
import useAppNotifications from "../../data/useAppNotifications";
import {
  chatQueryKeys,
  IChatUnreadChannel,
  useChatNotifications,
} from "../../data/useChat";
import { useAuth } from "../../contexts/AuthContext";
import useApiHelper from "../../hooks/useApiHelper";
import useChatDesktopNotifications from "../../hooks/useChatDesktopNotifications";
import classNames from "../../utils/classNames";
import { IChatUser } from "../../models/Chat";
import { DefaultResponseWithData } from "../../models/system";
import { fullNameFor, initialsFor } from "../../utils/chatHelpers";
import openInNewTab from "../../utils/openInNewTab";

const CHAT_PREFIX = "chat::";

export default function AppNotifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { get } = useApiHelper();
  const { loading: authLoading, loggedIn } = useAuth();

  const { workOrderNotifications } = useAppNotifications();
  const chat = useChatNotifications();

  // Mounting `useChatDesktopNotifications` here means it runs on every
  // authenticated route (the topbar is in `DashboardLayout`), so OS
  // notifications fire whether or not the user is on /chat.
  const desktop = useChatDesktopNotifications();

  // Reuse the chat users cache key so we share data with the chat
  // page when both are mounted, and pay only one fetch on dashboards
  // that never visit /chat.
  const usersQuery = useQuery({
    queryKey: chatQueryKeys.users,
    queryFn: () => get<DefaultResponseWithData<IChatUser[]>>("/chat/users"),
    staleTime: 1000 * 60,
    enabled: !authLoading && loggedIn,
  });

  const usersById = useMemo(() => {
    const map = new Map<string, IChatUser>();
    (usersQuery.data?.data ?? []).forEach((u) => map.set(u.id, u));
    return map;
  }, [usersQuery.data]);

  const chatRows: AppNotification[] = useMemo(() => {
    return chat.rows.map((row): AppNotification => {
      const author = row.latestAuthorId
        ? usersById.get(row.latestAuthorId) ?? null
        : null;
      const authorName = author ? fullNameFor(author) : "Someone";
      const initials = author ? initialsFor(author) : "??";

      // DMs: the author is by definition the other participant, so
      // we just lead with their name. Channels: prefix with the
      // channel name so the user can tell where the message came
      // from at a glance.
      const baseTitle =
        row.channelKind === "channel"
          ? `${authorName} in #${row.channelName}`
          : `${authorName}`;

      const title =
        row.unreadCount > 1
          ? `${baseTitle} (${row.unreadCount} new)`
          : baseTitle;

      return {
        id: `${CHAT_PREFIX}${row.channelId}`,
        title,
        description: (row.latestBody ?? "").trim() || undefined,
        createdAt: row.latestCreatedAt ?? new Date().toISOString(),
        unread: true,
        tone: "info",
        initials,
      };
    });
  }, [chat.rows, usersById]);

  const workOrderRows: AppNotification[] = workOrderNotifications.data?.data ?? [];

  const allNotifications = useMemo(() => {
    return [...chatRows, ...workOrderRows].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [chatRows, workOrderRows]);

  const handleClick = (notification: AppNotification) => {
    if (typeof notification.id === "string" && notification.id.startsWith(CHAT_PREFIX)) {
      const channelId = notification.id.slice(CHAT_PREFIX.length);

      // Optimistically pull the row out of the digest so the badge
      // count drops the instant the user clicks. The chat page will
      // mark-read on its own once it mounts and selects the channel,
      // and the next refetch will reconcile if anything changed.
      queryClient.setQueryData<DefaultResponseWithData<IChatUnreadChannel[]>>(
        chatQueryKeys.unreadChannels,
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.filter((r) => r.channelId !== channelId),
          };
        }
      );

      navigate(`/chat?channel=${channelId}`);
      return;
    }

    openInNewTab(`/work-orders`);
  };

  return (
    <NotificationsDropdown
      notifications={allNotifications}
      onNotificationClick={handleClick}
      headerExtra={<DesktopAlertsRow desktop={desktop} />}
    />
  );
}

/** Inline row in the notifications drawer that lets the user opt in to
 *  desktop notifications. Three states:
 *    - browser doesn't support them: row hidden
 *    - permission still default: button prompts `Notification.requestPermission()`
 *    - permission granted: shows a toggle for the local `enabled` flag
 *    - permission denied: shows a hint that the user has to fix it in browser settings
 */
function DesktopAlertsRow({
  desktop,
}: {
  desktop: ReturnType<typeof useChatDesktopNotifications>;
}) {
  if (!desktop.supported) return null;

  if (desktop.permission === "denied") {
    return (
      <div className="flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-700">
        <ComputerDesktopIcon className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Desktop alerts are blocked in this browser. Allow notifications in
          your site permissions and refresh.
        </span>
      </div>
    );
  }

  if (desktop.permission !== "granted") {
    return (
      <button
        type="button"
        onClick={() => {
          desktop.request();
        }}
        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-medium text-slate-700 transition hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
      >
        <span className="flex items-center gap-2">
          <ComputerDesktopIcon className="h-4 w-4" />
          Enable desktop alerts for new messages
        </span>
        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
          Off
        </span>
      </button>
    );
  }

  const active = desktop.enabled;

  return (
    <button
      type="button"
      onClick={() => desktop.setEnabled(!active)}
      className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-medium text-slate-700 transition hover:border-violet-200 hover:bg-violet-50"
    >
      <span className="flex items-center gap-2">
        <ComputerDesktopIcon className="h-4 w-4" />
        Desktop alerts {active ? "on" : "off"}
      </span>
      <span
        className={classNames(
          "inline-flex h-5 w-9 items-center rounded-full p-0.5 transition",
          active ? "bg-violet-500" : "bg-slate-200"
        )}
      >
        <span
          className={classNames(
            "h-4 w-4 rounded-full bg-white shadow transition-transform",
            active ? "translate-x-4" : "translate-x-0"
          )}
        />
      </span>
    </button>
  );
}
